"use client";

/**
 * OptionPayoffChart — payoff-at-expiry diagram for any multi-leg option
 * strategy, computed purely from the registered legs (action, right, strike,
 * entry premium, quantity). Generic by design: strangles, straddles, condors,
 * verticals and future strategies all render without changes.
 *
 * Profit region is shaded emerald, loss regions red, with breakeven markers
 * and strike reference ticks. Values are per contract (×100 multiplier).
 */

import { useMemo } from "react";
import type { TradeLeg } from "@/lib/mock/signal-performance";

const W = 640;
const H = 220;
const PAD = { top: 16, right: 14, bottom: 28, left: 56 };

function legPnlAt(leg: TradeLeg, spot: number): number {
  if (leg.kind !== "option" || leg.strike == null) return 0;
  const intrinsic =
    leg.right === "call"
      ? Math.max(0, spot - leg.strike)
      : Math.max(0, leg.strike - spot);
  const qty = leg.quantity ?? 1;
  const prem = leg.entry ?? 0;
  const perShare = leg.action === "sell" ? prem - intrinsic : intrinsic - prem;
  return perShare * qty * 100;
}

export function OptionPayoffChart({
  legs,
  title,
}: {
  legs: TradeLeg[];
  title?: string;
}) {
  const model = useMemo(() => {
    const optLegs = legs.filter((l) => l.kind === "option" && l.strike != null);
    if (optLegs.length === 0) return null;
    const strikes = optLegs.map((l) => l.strike as number);
    const lo = Math.min(...strikes);
    const hi = Math.max(...strikes);
    const span = Math.max(hi - lo, hi * 0.08);
    const xMin = Math.max(0, lo - span * 0.6);
    const xMax = hi + span * 0.6;

    const N = 240;
    const pts: Array<{ s: number; p: number }> = [];
    for (let i = 0; i <= N; i++) {
      const s = xMin + ((xMax - xMin) * i) / N;
      pts.push({ s, p: optLegs.reduce((acc, l) => acc + legPnlAt(l, s), 0) });
    }
    const pMin = Math.min(...pts.map((d) => d.p));
    const pMax = Math.max(...pts.map((d) => d.p));
    const pad = Math.max((pMax - pMin) * 0.15, 1);

    // Breakevens: sign changes between samples, linearly interpolated.
    const breakevens: number[] = [];
    for (let i = 1; i < pts.length; i++) {
      const a = pts[i - 1];
      const b = pts[i];
      if ((a.p <= 0 && b.p > 0) || (a.p >= 0 && b.p < 0)) {
        const t = Math.abs(a.p) / (Math.abs(a.p) + Math.abs(b.p) || 1);
        breakevens.push(a.s + (b.s - a.s) * t);
      }
    }
    return {
      pts, strikes, breakevens,
      xMin, xMax, yMin: pMin - pad, yMax: pMax + pad,
      maxProfit: pMax, maxLoss: pMin,
    };
  }, [legs]);

  if (!model) return null;

  const ix = (s: number) =>
    PAD.left + ((s - model.xMin) / (model.xMax - model.xMin)) * (W - PAD.left - PAD.right);
  const iy = (p: number) =>
    PAD.top + ((model.yMax - p) / (model.yMax - model.yMin)) * (H - PAD.top - PAD.bottom);

  const zeroY = iy(0);
  const line = model.pts.map((d, i) => `${i ? "L" : "M"}${ix(d.s).toFixed(1)},${iy(d.p).toFixed(1)}`).join("");
  // Area between curve and the zero line; clipped per-half for the two tints.
  const area =
    line +
    `L${ix(model.xMax).toFixed(1)},${zeroY.toFixed(1)}` +
    `L${ix(model.xMin).toFixed(1)},${zeroY.toFixed(1)}Z`;

  const fmt = (v: number) =>
    `${v < 0 ? "-" : ""}$${Math.abs(v) >= 1000 ? `${(Math.abs(v) / 1000).toFixed(1)}k` : Math.abs(v).toFixed(0)}`;

  return (
    <div className="rounded-sm border border-white/10 bg-black/40 p-3">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-[10.5px] font-bold uppercase tracking-widest text-white/70">
          {title ?? "Payoff at expiry (per contract)"}
        </span>
        <span className="text-[10.5px] tabular-nums text-white/60">
          Max win <span className="text-emerald-300 font-bold">{fmt(model.maxProfit)}</span>
          {" · "}
          Max shown loss <span className="text-red-300 font-bold">{fmt(model.maxLoss)}</span>
        </span>
      </div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto">
        <defs>
          <clipPath id="payoff-profit-clip">
            <rect x={0} y={0} width={W} height={zeroY} />
          </clipPath>
          <clipPath id="payoff-loss-clip">
            <rect x={0} y={zeroY} width={W} height={H - zeroY} />
          </clipPath>
        </defs>

        {/* zero line */}
        <line x1={PAD.left} x2={W - PAD.right} y1={zeroY} y2={zeroY}
          stroke="rgba(255,255,255,0.25)" strokeDasharray="3 3" />

        {/* profit / loss shading */}
        <path d={area} fill="rgba(52,211,153,0.18)" clipPath="url(#payoff-profit-clip)" />
        <path d={area} fill="rgba(248,113,113,0.16)" clipPath="url(#payoff-loss-clip)" />

        {/* payoff curve */}
        <path d={line} fill="none" stroke="#34d399" strokeWidth={2} />

        {/* strikes */}
        {model.strikes.map((k) => (
          <g key={`k-${k}`}>
            <line x1={ix(k)} x2={ix(k)} y1={PAD.top} y2={H - PAD.bottom}
              stroke="rgba(249,115,22,0.45)" strokeDasharray="2 4" />
            <text x={ix(k)} y={H - 10} textAnchor="middle"
              className="fill-orange-300" fontSize={10.5} fontWeight={700}>
              {k}
            </text>
          </g>
        ))}

        {/* breakevens */}
        {model.breakevens.map((b) => (
          <g key={`b-${b.toFixed(2)}`}>
            <line x1={ix(b)} x2={ix(b)} y1={PAD.top} y2={H - PAD.bottom}
              stroke="rgba(234,179,8,0.6)" />
            <text x={ix(b)} y={PAD.top + 10} textAnchor="middle"
              className="fill-yellow-300" fontSize={10} fontWeight={700}>
              BE {b.toFixed(2)}
            </text>
          </g>
        ))}

        {/* y axis labels */}
        {[model.yMax, 0, model.yMin].map((v) => (
          <text key={`y-${v.toFixed(1)}`} x={PAD.left - 6} y={iy(v) + 3.5}
            textAnchor="end" className="fill-white/50" fontSize={10}>
            {fmt(v)}
          </text>
        ))}
      </svg>
    </div>
  );
}
